import React, { useState, useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { MenuItem, TextField as MuiTextField } from '@mui/material';
import Select from 'react-select';
import { listTz, findTzByName } from 'timezone-select-js';
import { getActiveOrganizationSelector, getOrganizationFeaturesSelector } from '@redux/organization/organizationSelectors';
import { getOrganizationFeaturesThunk, setOrganizationFeatureThunk, updateOrganizationThunk, updateOrganizationLocationThunk } from '@redux/organization/organizationThunks';
import { FEATURE_GAMIFICATION_EMAIL } from '@redux/organization/organizationTypes';
import { ReduxState } from '@redux/types';
import { useAlert } from 'components/AlertInfoGlobal/useAlert';
import { Button } from '../../../components/Button/Button';
import { TextField } from '../../../components/TextField';
import { Text } from '../../../components/Typography/Text';
import FeatureToggle from './components/FeatureToggle';
import style from './General.scss';

// Type for timezone option
type TimezoneOption = {
  value: string;
  label: string;
  offset?: number;
};

// US States for dropdown - codes as values, full names as labels
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

type PropsFromRedux = ConnectedProps<typeof connectRedux>;
type Props = PropsFromRedux & {};

const General: React.FC<Props> = ({ activeOrganization, organizationFeatures, updateOrganization, updateOrganizationLocation, getOrganizationFeatures, setOrganizationFeature }) => {
  const [organizationName, setOrganizationName] = useState(activeOrganization?.name || '');
  const [organizationNameError, setOrganizationNameError] = useState('');
  const [gamificationEmailEnabled, setGamificationEmailEnabled] = useState(false);
  const [originalGamificationEmailEnabled, setOriginalGamificationEmailEnabled] = useState(false);
  const [timezone, setTimezone] = useState<TimezoneOption | null>(null);
  const [city, setCity] = useState(activeOrganization?.locationCity || '');
  const [state, setState] = useState(activeOrganization?.locationRegion || '');
  const { showAlertInfo, showAlertWarning } = useAlert();

  // Get timezone options
  const timezoneOptions = useMemo(() => listTz(), []);

  // Map account type from backend to display value
  const getAccountTypeDisplay = (accountType?: string): string => {
    if (accountType === 'DTC') {
      return 'Direct to Consumer';
    }
    if (accountType === 'ENTERPRISE') {
      return 'Enterprise';
    }
    // Default to Enterprise if no value
    return 'Enterprise';
  };

  // Custom styles for react-select to match design system
  const selectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: '44px',
      height: '44px',
      fontFamily: 'Outfit',
      fontSize: '16px',
      borderRadius: '8px',
      borderColor: '#D0D5DD',
      '&:hover': {
        borderColor: '#D0D5DD'
      }
    }),
    valueContainer: (base: any) => ({
      ...base,
      height: '44px',
      padding: '0 12px',
      fontFamily: 'Outfit'
    }),
    input: (base: any) => ({
      ...base,
      margin: '0px',
      fontFamily: 'Outfit'
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: '44px'
    }),
    menu: (base: any) => ({
      ...base,
      fontFamily: 'Outfit'
    }),
    option: (base: any) => ({
      ...base,
      fontFamily: 'Outfit',
      fontSize: '16px'
    }),
    singleValue: (base: any) => ({
      ...base,
      fontFamily: 'Outfit'
    }),
    placeholder: (base: any) => ({
      ...base,
      fontFamily: 'Outfit',
      color: '#66768F'
    })
  };

  // Load organization features when component mounts or organization changes
  useEffect(() => {
    if (activeOrganization?.id) {
      getOrganizationFeatures(activeOrganization.id);
    }
  }, [activeOrganization?.id, getOrganizationFeatures]);

  // Update state when activeOrganization changes
  useEffect(() => {
    const name = activeOrganization?.name || '';
    setOrganizationName(name);

    // Validate organization name on load
    if (name.length < 3) {
      setOrganizationNameError('Organization name must be at least 3 characters');
    } else {
      setOrganizationNameError('');
    }

    setCity(activeOrganization?.locationCity || '');
    setState(activeOrganization?.locationRegion || '');

    // Find timezone option from string
    if (activeOrganization?.timezone) {
      const tzOption = findTzByName(activeOrganization.timezone, timezoneOptions);
      setTimezone(tzOption || null);
    } else {
      setTimezone(null);
    }
  }, [activeOrganization, timezoneOptions]);

  // Update gamification email state based on features
  useEffect(() => {
    const gamificationFeature = organizationFeatures?.find((f) => f.featureId === FEATURE_GAMIFICATION_EMAIL);
    const enabled = gamificationFeature?.enabled ?? false;
    setGamificationEmailEnabled(enabled);
    setOriginalGamificationEmailEnabled(enabled);
  }, [organizationFeatures]);

  // Validation function for organization name
  const validateOrganizationName = (name: string) => {
    if (name.length < 3) {
      setOrganizationNameError('Organization name must be at least 3 characters');
    } else {
      setOrganizationNameError('');
    }
  };

  const handleNameChange = (e) => {
    const newName = e.target.value;
    setOrganizationName(newName);
    validateOrganizationName(newName);
  };

  const handleCityChange = (e) => {
    setCity(e.target.value);
  };

  const handleStateChange = (e) => {
    setState(e.target.value);
  };

  const handleGamificationEmailToggle = (enabled: boolean) => {
    setGamificationEmailEnabled(enabled);
  };

  const handleCancel = () => {
    // Reset all fields to their original values
    setOrganizationName(activeOrganization?.name || '');
    setOrganizationNameError('');
    setCity(activeOrganization?.locationCity || '');
    setState(activeOrganization?.locationRegion || '');

    // Reset timezone
    if (activeOrganization?.timezone) {
      const tzOption = findTzByName(activeOrganization.timezone, timezoneOptions);
      setTimezone(tzOption || null);
    } else {
      setTimezone(null);
    }

    const gamificationFeature = organizationFeatures?.find((f) => f.featureId === FEATURE_GAMIFICATION_EMAIL);
    setGamificationEmailEnabled(gamificationFeature?.enabled ?? false);
  };

  const handleUpdate = async () => {
    if (!activeOrganization) return;

    // Validate organization name before submitting
    if (organizationNameError) {
      showAlertWarning({
        title: 'Validation Error',
        message: organizationNameError
      });
      return;
    }

    // Extract timezone value from TimezoneOption object
    const timezoneValue = timezone?.value || '';

    // Check what has changed
    const hasNameChanged = organizationName !== activeOrganization.name;
    const hasGamificationEmailChanged = gamificationEmailEnabled !== originalGamificationEmailEnabled;
    const hasTimezoneChanged = timezoneValue !== (activeOrganization.timezone || '');
    const hasCityChanged = city !== (activeOrganization.locationCity || '');
    const hasStateChanged = state !== (activeOrganization.locationRegion || '');
    const hasLocationChanged = hasTimezoneChanged || hasCityChanged || hasStateChanged;

    // Check if there are any changes
    if (!hasNameChanged && !hasGamificationEmailChanged && !hasLocationChanged) {
      showAlertWarning({
        title: 'No changes',
        message: 'No changes detected to save.'
      });
      return;
    }

    try {
      // Update organization name if changed
      if (hasNameChanged) {
        await updateOrganization({
          ...activeOrganization,
          name: organizationName
        });
      }

      // Update location if changed
      if (hasLocationChanged) {
        await updateOrganizationLocation(activeOrganization.id, {
          timezone: timezoneValue || undefined,
          locationCity: city || undefined,
          locationRegion: state || undefined
        });
      }

      // Update gamification email feature if changed
      if (hasGamificationEmailChanged) {
        await setOrganizationFeature(activeOrganization.id, FEATURE_GAMIFICATION_EMAIL, gamificationEmailEnabled);
        // Update the original value after successful save
        setOriginalGamificationEmailEnabled(gamificationEmailEnabled);
      }

      // Show success message
      showAlertInfo({
        title: 'Success!',
        message: 'Organization settings updated successfully.'
      });
    } catch (error) {
      console.error('Failed to update organization:', error);
      showAlertWarning({
        title: 'Error',
        message: 'Failed to update organization settings. Please try again.'
      });
    }
  };

  return (
    <div className={style.container}>
      <div className={style.leftContent}>
        <Text className={style.sectionHeader}>Customize</Text>

        <div className={style.section}>
          <Text variant="sm" className={style.fieldLabel}>
            Name
          </Text>
          <TextField
            name="organizationName"
            value={organizationName}
            onChange={handleNameChange}
            placeholder="Organization Name"
            autoComplete="off"
            error={organizationNameError}
          />
        </div>

        <Text className={style.sectionHeader}>System defaults</Text>

        <div className={style.section}>
          <Text variant="sm" className={style.fieldLabel}>
            Timezone
          </Text>
          <Select value={timezone} onChange={(option) => setTimezone(option as TimezoneOption | null)} options={timezoneOptions} placeholder="Select Timezone" styles={selectStyles} />
        </div>

        <div className={style.locationRow}>
          <div className={style.locationField}>
            <Text variant="sm" className={style.fieldLabel}>
              City
            </Text>
            <TextField name="city" value={city} onChange={handleCityChange} placeholder="City" autoComplete="off" />
          </div>

          <div className={style.locationField}>
            <Text variant="sm" className={style.fieldLabel}>
              State
            </Text>
            <MuiTextField
              name="state"
              value={state}
              onChange={handleStateChange}
              autoComplete="off"
              select
              fullWidth
              variant="outlined"
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected: unknown): React.ReactNode => {
                  if (!selected || selected === '') {
                    return <span style={{ color: '#66768F' }}>State</span>;
                  }
                  const selectedState = US_STATES.find(s => s.value === selected);
                  return selectedState?.label || (selected as string);
                }
              }}
              sx={{
                '& .MuiInputBase-root': {
                  height: '44px',
                  fontFamily: 'Outfit',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center'
                },
                '& .MuiSelect-select': {
                  fontFamily: 'Outfit',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  paddingTop: '10px',
                  paddingBottom: '10px'
                },
                '& .MuiMenuItem-root': {
                  fontFamily: 'Outfit',
                  fontSize: '16px'
                }
              }}
            >
              {US_STATES.map((usState) => (
                <MenuItem
                  key={usState.value}
                  value={usState.value}
                  sx={{
                    fontFamily: 'Outfit',
                    fontSize: '16px'
                  }}
                >
                  {usState.label}
                </MenuItem>
              ))}
            </MuiTextField>
          </div>
        </div>

        <Text className={style.sectionHeader}>Features</Text>

        <div className={style.featuresContainer}>
          <FeatureToggle
            title="Enable Gamification"
            subtitle="Weekly Luna score boosting engagement and personalization."
            enabled={gamificationEmailEnabled}
            onChange={handleGamificationEmailToggle}
          />
        </div>

        <div className={style.buttonContainer}>
          <Button variant="outlined" color="primary" className={style.cancelButton} onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" className={style.updateButton} onClick={handleUpdate} disabled={!!organizationNameError}>
            Update
          </Button>
        </div>
      </div>

      <div className={style.rightContent}>
        <div className={style.detailsBox}>
          <Text className={style.detailsTitle}>Organization details</Text>

          <div className={style.detailsSection}>
            <Text variant="sm" className={style.detailsLabel}>
              Name
            </Text>
            <Text variant="md" className={style.detailsValue}>
              {activeOrganization?.name || 'N/A'}
            </Text>
          </div>

          <div className={style.detailsSection}>
            <Text variant="sm" className={style.detailsLabel}>
              ID
            </Text>
            <Text variant="md" className={style.detailsValue}>
              {activeOrganization?.id || 'N/A'}
            </Text>
          </div>

          <div className={style.detailsSection}>
            <Text variant="sm" className={style.detailsLabel}>
              Account
            </Text>
            <Text variant="md" className={style.detailsValue}>
              {getAccountTypeDisplay(activeOrganization?.accountType)}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    activeOrganization: getActiveOrganizationSelector(state),
    organizationFeatures: getOrganizationFeaturesSelector(state)
  }),
  {
    updateOrganization: updateOrganizationThunk,
    updateOrganizationLocation: updateOrganizationLocationThunk,
    getOrganizationFeatures: getOrganizationFeaturesThunk,
    setOrganizationFeature: setOrganizationFeatureThunk
  }
);

export default compose(connectRedux)(General);
